/**
 * @file Modal.tsx
 * @description 项目通用 Modal，基于 antd Modal 封装，支持全量 props 透传
 * @author wangwei149
 * @date 2026-04-20
 */

import {useState} from 'react';
import {Modal as AntdModal} from 'antd';
import type {ModalProps as AntdModalProps, ModalFuncProps} from 'antd';
import type {HookAPI, ModalFuncWithPromise} from 'antd/lib/modal/useModal';
import {createCx, mergeSemanticClassNames} from '@utils/cx';
import styles from './index.module.less';
import Button from '@/components/common/Button';
import Icon from '@/components/common/Icon';

const cx = createCx(styles);

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

// UI要求固定这四种尺寸，不允许更改
const MODAL_WIDTH: Record<ModalSize, number> = {
    sm: 440,
    md: 640,
    lg: 850,
    xl: 980,
};

const CONTAINER_CLASS: Record<ModalSize, string> = {
    sm: 'containerSm',
    md: 'containerMd',
    lg: 'containerLg',
    xl: 'containerXl',
};

// todo换成icon
const closeIcon = (
    <img
        src="https://now.bdstatic.com/stash/v1/01e9009/mangaflow-fe/9896fde/workspace/BjhBasicGuanbi.png"
        width={16}
        height={16}
        alt=""
    />
);
const MODAL_ICONS: Record<'success' | 'warning' | 'error' | 'info', React.ReactNode> = {
    success: <Icon name="AijBasicChenggong" size={16} />,
    warning: <Icon name="AijBasicTishiyellow" size={16} />,
    error: <Icon name="AijBasicTishired" size={16} />,
    info: <Icon name="AijBasicTishiblue" size={16} />,
};

// 扩展 antd classNames，支持 root 键（替代 rootClassName prop）
type ModalClassNames = AntdModalProps['classNames'] & {root?: string};

interface ModalProps extends Omit<AntdModalProps, 'rootClassName' | 'classNames'> {
    size?: ModalSize;
    subTitle?: React.ReactNode;
    classNames?: ModalClassNames;
}

const Modal: React.FC<ModalProps> = ({
    size = 'sm',
    subTitle,
    title,
    footer,
    classNames,
    onOk,
    onCancel,
    okText = '确定',
    cancelText = '取消',
    okButtonProps,
    cancelButtonProps,
    confirmLoading,
    ...restProps
}) => {
    const baseClassNames = {
        root: cx('root'),
        body: cx('body'),
        footer: footer === null ? undefined : cx('footer'),
        container: cx(CONTAINER_CLASS[size]),
        mask: cx('mask'),
    };
    const mergedClassNames = mergeSemanticClassNames(baseClassNames, classNames);
    const {root: rootClassName, ...antdClassNames} = mergedClassNames;

    // title 和 subTitle 都为空时不渲染 title 区域
    const titleNode =
        title || subTitle ? (
            <>
                {title}
                {subTitle && <div className={cx('subTitle')}>{subTitle}</div>}
            </>
        ) : undefined;

    const defaultFooter = (
        <>
            <Button
                {...(cancelButtonProps as React.ComponentProps<typeof Button>)}
                onClick={(e) => {
                    cancelButtonProps?.onClick?.(e);
                    onCancel?.(e as React.MouseEvent<HTMLButtonElement>);
                }}
            >
                {cancelText}
            </Button>
            <Button
                type="primary"
                loading={confirmLoading}
                {...(okButtonProps as React.ComponentProps<typeof Button>)}
                onClick={(e) => {
                    okButtonProps?.onClick?.(e);
                    onOk?.(e as React.MouseEvent<HTMLButtonElement>);
                }}
            >
                {okText}
            </Button>
        </>
    );

    return (
        <AntdModal
            closeIcon={closeIcon}
            width={MODAL_WIDTH[size]}
            title={titleNode}
            centered
            footer={footer !== undefined ? footer : defaultFooter}
            classNames={antdClassNames}
            rootClassName={rootClassName}
            onCancel={onCancel}
            {...restProps}
        />
    );
};

// 构造与封装 Modal 一致的自定义 footer（支持 async 回调自动 loading）
const ConfirmFooter: React.FC<{config: ModalFuncProps; close: () => void}> = ({config, close}) => {
    const {onOk, onCancel, okText = '确定', cancelText = '取消'} = config;
    const [okLoading, setOkLoading] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);

    const handleCancel = async () => {
        setCancelLoading(true);
        try {
            await onCancel?.();
            close();
        } catch {
            // 取消回调异常时保留弹窗，避免阻断用户继续操作。
        } finally {
            setCancelLoading(false);
        }
    };
    const handleOk = async () => {
        setOkLoading(true);
        try {
            await onOk?.();
            close();
        } catch {
            // 业务回调自行提示错误，异常时保留弹窗。
        } finally {
            setOkLoading(false);
        }
    };

    return (
        <div className={cx('footer')}>
            <Button
                {...(config.cancelButtonProps as React.ComponentProps<typeof Button>)}
                onClick={handleCancel}
                loading={cancelLoading}
                disabled={okLoading}
            >
                {cancelText}
            </Button>
            <Button
                {...(config.okButtonProps as React.ComponentProps<typeof Button>)}
                type="primary"
                onClick={handleOk}
                loading={okLoading}
                disabled={cancelLoading}
            >
                {okText}
            </Button>
        </div>
    );
};

function withModalDefaults(
    config: ModalFuncProps,
    close: () => void,
    type: 'success' | 'warning' | 'error' | 'info',
): ModalFuncProps {
    const baseClassNames = {
        root: cx('root', config.rootClassName),
        body: cx('body'),
        footer: cx('footer'),
        mask: cx('mask'),
    };
    const mergedClassNames = mergeSemanticClassNames(
        baseClassNames,
        config.classNames as Record<string, string> | undefined,
    );
    const {root: rootClassName, ...antdClassNames} = mergedClassNames;

    return {
        width: MODAL_WIDTH.sm,
        closable: true,
        centered: true, // 所有静态确认弹窗统一居中，业务无需各自传
        closeIcon,
        icon: MODAL_ICONS[type],
        ...config,
        onCancel: close, // 右上角关闭只关闭弹窗
        rootClassName,
        classNames: antdClassNames,
        footer:
            config.footer !== undefined ? (
                config.footer
            ) : (
                <ConfirmFooter config={config} close={close} />
            ),
    };
}

/**
 * 包装模态框方法的高阶函数
 *
 * 用于为模态框方法添加默认配置和销毁能力，支持 success、warning、error、info 类型
 *
 * @template T - 模态框实例类型，必须包含 destroy 方法
 * @param method - 模态框创建方法，接收配置并返回模态框实例
 * @param type - 模态框类型
 * @returns 返回一个包装后的模态框方法函数
 */
function wrapModalMethod<T extends {destroy: () => void}>(
    method: (config: ModalFuncProps) => T,
    type: 'success' | 'warning' | 'error' | 'info',
) {
    return (config: ModalFuncProps): T & Pick<ReturnType<ModalFuncWithPromise>, 'then'> => {
        const holder: {current?: T} = {current: undefined};
        let resolveConfirmation: (confirmed: boolean) => void = () => undefined;
        let rejectConfirmation: (error: unknown) => void = () => undefined;
        const confirmation = new Promise<boolean>((resolve, reject) => {
            resolveConfirmation = resolve;
            rejectConfirmation = reject;
        });
        // 调用方可能不消费返回值（fire-and-forget），兜底避免 unhandled rejection
        confirmation.catch(() => undefined);
        const close = () => {
            resolveConfirmation(false);
            holder.current?.destroy();
        };
        const wrappedConfig: ModalFuncProps = {
            ...config,
            onOk: async () => {
                try {
                    await config.onOk?.();
                    resolveConfirmation(true);
                } catch (error) {
                    rejectConfirmation(error);
                    throw error;
                }
            },
            onCancel: async () => {
                try {
                    await config.onCancel?.();
                    resolveConfirmation(false);
                } catch (error) {
                    rejectConfirmation(error);
                    throw error;
                }
            },
        };
        const instance = method(withModalDefaults(wrappedConfig, close, type));
        const then = ((resolve: (confirmed: boolean) => unknown, reject: VoidFunction) =>
            confirmation.then(
                resolve,
                reject,
            )) as unknown as ReturnType<ModalFuncWithPromise>['then'];
        holder.current = instance;
        return Object.assign(instance, {then}) as T &
            Pick<ReturnType<ModalFuncWithPromise>, 'then'>;
    };
}

// 封装 useModal，让 modal.confirm/info 等方法自动注入封装后的样式和 footer
function useModal(): [HookAPI, React.ReactNode] {
    const [modal, contextHolder] = AntdModal.useModal();

    const wrappedModal: HookAPI = {
        confirm: wrapModalMethod((c) => modal.confirm(c), 'info'),
        info: wrapModalMethod((c) => modal.info(c), 'info'),
        warning: wrapModalMethod((c) => modal.warning(c), 'warning'),
        error: wrapModalMethod((c) => modal.error(c), 'error'),
        success: wrapModalMethod((c) => modal.success(c), 'success'),
    };

    return [wrappedModal, contextHolder];
}

// 将 antd Modal 的静态方法/属性挂载到自定义 Modal 上，同时应用封装样式
const ModalCompat = Object.assign(Modal, {
    useModal,
    confirm: wrapModalMethod((c) => AntdModal.confirm(c), 'info'),
    info: wrapModalMethod((c) => AntdModal.info(c), 'info'),
    warning: wrapModalMethod((c) => AntdModal.warning(c), 'warning'),
    error: wrapModalMethod((c) => AntdModal.error(c), 'error'),
    success: wrapModalMethod((c) => AntdModal.success(c), 'success'),
    destroyAll: AntdModal.destroyAll,
    config: AntdModal.config,
});

export default ModalCompat;
